// src/components/ResumeBuilder/forms/EducationForm.jsx
import React, { useContext } from "react";
import {
  Box,
  Card,
  Grid,
  TextField,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import { FieldArray, Formik, Form } from "formik";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import GradientButton from "../Button";
import { ResumeContext } from "../../ResumeContext";
import { saveResumeData } from "../../api/resumeapi";
import BasicInfoSkeleton from "./skeletonLoader";
import { EducationSchema } from "./resumevalidation";

export default function EducationForm({ onNext }) {
  const { data, setData, loading } = useContext(ResumeContext);

  // ✅ Skeleton while loading
  if (loading || !data) {
    return <BasicInfoSkeleton />;
  }

  return (
    <Card sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
      <Typography fontSize={13} color="#6E6D81" mb={2}>
        Share your academic qualifications!
      </Typography>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Formik
          enableReinitialize
          initialValues={{
            education:
              Array.isArray(data?.educationArr) &&
              data.educationArr.length > 0
                ? data.educationArr.map((edu) => ({
                    course: edu.course || "",
                    institute: edu.institute || "",
                    percentage: edu.percentage || "",
                    startDate: edu.start
                      ? dayjs(edu.start, "DD/MM/YYYY")
                      : null,
                    endDate: edu.end
                      ? dayjs(edu.end, "DD/MM/YYYY")
                      : null,
                    description: edu.description || "",
                  }))
                : [
                    {
                      course: "",
                      institute: "",
                      percentage: "",
                      startDate: null,
                      endDate: null,
                      description: "",
                    },
                  ],
          }}
          validationSchema={EducationSchema}
          onSubmit={async (values) => {
            const educationArr = values.education.map((edu) => ({
              course: edu.course,
              institute: edu.institute,
              percentage: edu.percentage,
              start: edu.startDate
                ? edu.startDate.format("DD/MM/YYYY")
                : "",
              end: edu.endDate
                ? edu.endDate.format("DD/MM/YYYY")
                : "",
              description: edu.description,
            }));

            const payload = {
              ...data,
              educationArr,
            };

            try {
              // SAVE TO DB
              console.log("Saving Education:", values.education,payload);
              await saveResumeData(payload);

              // UPDATE CONTEXT
              setData((prev) => ({
                ...prev,
                educationArr,
              }));
            } catch (err) {
              console.error("EDUCATION SAVE ERROR:", err);
            }
          }}
        >
          {({ values, handleChange, setFieldValue }) => (
            <Form>
              <FieldArray name="education">
                {({ push, remove }) => (
                  <Box>
                    {values.education.map((edu, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          mb: 2,
                          borderBottom: "1px dashed #eee",
                          pb: 2,
                        }}
                      >
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              fullWidth
                              label="Course / Degree"
                              name={`education[${idx}].course`}
                              size="small"
                              value={edu.course}
                              onChange={handleChange}
                            />
                          </Grid>

                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              fullWidth
                              label="Institute"
                              name={`education[${idx}].institute`}
                              size="small"
                              value={edu.institute}
                              onChange={handleChange}
                            />
                          </Grid>

                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              fullWidth
                              label="Percentage / CGPA"
                              name={`education[${idx}].percentage`}
                              size="small"
                              value={edu.percentage}
                              onChange={handleChange}
                            />
                          </Grid>

                          <Grid size={{ xs: 12, sm: 6 }}>
                            <DatePicker
                              label="Start Date"
                              value={edu.startDate}
                              onChange={(date) =>
                                setFieldValue(
                                  `education[${idx}].startDate`,
                                  date
                                )
                              }
                              slotProps={{
                                textField: {
                                  size: "small",
                                  fullWidth: true,
                                },
                              }}
                            />
                          </Grid>

                          <Grid size={{ xs: 12, sm: 6 }}>
                            <DatePicker
                              label="End Date"
                              value={edu.endDate}
                              onChange={(date) =>
                                setFieldValue(
                                  `education[${idx}].endDate`,
                                  date
                                )
                              }
                              slotProps={{
                                textField: {
                                  size: "small",
                                  fullWidth: true,
                                },
                              }}
                            />
                          </Grid>

                          <Grid size={12}>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              label="Description"
                              name={`education[${idx}].description`}
                              size="small"
                              value={edu.description}
                              onChange={handleChange}
                            />
                          </Grid>

                          <Grid size={12} textAlign="right">
                            <IconButton
                              onClick={() => remove(idx)}
                              color="error"
                              disabled={values.education.length === 1}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </Box>
                    ))}

                    <Button
                      startIcon={<AddIcon />}
                      onClick={() =>
                        push({
                          course: "",
                          institute: "",
                          percentage: "",
                          startDate: null,
                          endDate: null,
                          description: "",
                        })
                      }
                    >
                      Add education
                    </Button>
                  </Box>
                )}
              </FieldArray>

              {/* BUTTONS */}
              <Box mt={3} display="flex" justifyContent="center" gap={4}>
                <GradientButton
                  type="button"
                  onClick={onNext}
                >
                  Next
                </GradientButton>

                <GradientButton type="submit">
                  Save
                </GradientButton>
              </Box>
            </Form>
          )}
        </Formik>
      </LocalizationProvider>
    </Card>
  );
}
